/**
 * Pokesume - Pokemon Career Training Game
 *
 * Modular Architecture Version
 * - Server-authoritative gameplay
 * - Mandatory authentication
 * - Separated concerns and modular components
 */

import React from 'react';
import { AppProviders } from './contexts/AppProviders';
import { useGame } from './contexts/GameContext';
import { useGameData } from './contexts/GameDataContext';
import AuthWrapper from './components/AuthWrapper';
import BattleController from './components/BattleController';

// Import screens
import MenuScreen from './screens/MenuScreen';
import PokemonSelectionScreen from './screens/PokemonSelectionScreen';
import InspirationSelectionScreen from './screens/InspirationSelectionScreen';
import SupportSelectionScreen from './screens/SupportSelectionScreen';
import MyPokemonScreen from './screens/MyPokemonScreen';
import MySupportScreen from './screens/MySupportScreen';
import TrainedPokemonScreen from './screens/TrainedPokemonScreen';
import GachaScreen from './screens/GachaScreen';
import SupportGachaScreen from './screens/SupportGachaScreen';
import VictoryScreen from './screens/VictoryScreen';
import GameOverScreen from './screens/GameOverScreen';
import CareerEndScreen from './screens/CareerEndScreen';
import BattleScreen from './screens/BattleScreen';
import CareerScreen from './screens/CareerScreen';
import HistoryScreen from './screens/HistoryScreen';
import TournamentsScreen from './screens/TournamentsScreen';
import TournamentDetailsScreen from './screens/TournamentDetailsScreen';
import TournamentBracketScreen from './screens/TournamentBracketScreen';
import TournamentReplayScreen from './screens/TournamentReplayScreen';
import PvPScreen from './screens/PvPScreen';
import PvPTeamSelectScreen from './screens/PvPTeamSelectScreen';
import PvPQueueScreen from './screens/PvPQueueScreen';
import PvPReplayScreen from './screens/PvPReplayScreen';
import PvPLeaderboardScreen from './screens/PvPLeaderboardScreen';
import GameGuideScreen from './screens/GameGuideScreen';
import ProfileScreen from './screens/ProfileScreen';
import DebugSpriteTuner from './screens/DebugSpriteTuner';
import TrialsScreen from './screens/TrialsScreen';
import TrialDetailScreen from './screens/TrialDetailScreen';
import TrialBattleScreen from './screens/TrialBattleScreen';

/**
 * GameRouter
 *
 * Routes to different screens based on gameState
 */
const GameRouter = () => {
  const { gameState, setGameState } = useGame();

  // Redirect unknown states to menu
  React.useEffect(() => {
    const validStates = [
      'menu', 'pokemonSelect', 'pokemonSelection', 'inspirationSelect', 'inspirationSelection',
      'supportSelect', 'supportSelection', 'myPokemon', 'pokemonInventory', 'mySupports',
      'supportInventory', 'trainedPokemon', 'gacha', 'supportGacha', 'career', 'battle',
      'victory', 'gameOver', 'careerEnd', 'history', 'tournaments', 'tournamentDetails',
      'tournamentBracket', 'tournamentReplay', 'pvp', 'pvpTeamSelect', 'pvpQueue', 'pvpReplay',
      'pvpLeaderboard', 'guide', 'profile', 'debugSprites', 'trials', 'trialDetail', 'trialBattle'
    ];

    if (!validStates.includes(gameState)) {
      console.warn(`Unknown game state: ${gameState}, redirecting to menu`);
      setGameState('menu');
    }
  }, [gameState, setGameState]);

  // Routing to different screens based on gameState
  switch (gameState) {
    case 'menu':
      return <MenuScreen />;

    case 'pokemonSelect':
    case 'pokemonSelection':
      return <PokemonSelectionScreen />;

    case 'inspirationSelect':
    case 'inspirationSelection':
      return <InspirationSelectionScreen />;

    case 'supportSelect':
    case 'supportSelection':
      return <SupportSelectionScreen />;

    // Inventory Screens
    case 'myPokemon':
    case 'pokemonInventory':
      return <MyPokemonScreen />;

    case 'mySupports':
    case 'supportInventory':
      return <MySupportScreen />;

    case 'trainedPokemon':
      return <TrainedPokemonScreen />;

    // Gacha Screens
    case 'gacha':
      return <GachaScreen />;

    case 'supportGacha':
      return <SupportGachaScreen />;

    // Career and Battle Screens
    case 'career':
      return <CareerScreen />;

    case 'battle':
      return <BattleScreen />;

    case 'victory':
      return <VictoryScreen />;

    case 'gameOver':
      return <GameOverScreen />;

    case 'careerEnd':
      return <CareerEndScreen />;

    // History Screen
    case 'history':
      return <HistoryScreen />;

    // Tournament Screens
    case 'tournaments':
      return <TournamentsScreen />;

    case 'tournamentDetails':
      return <TournamentDetailsScreen />;

    case 'tournamentBracket':
      return <TournamentBracketScreen />;

    case 'tournamentReplay':
      return <TournamentReplayScreen />;

    // PvP Matchmaking Screens
    case 'pvp':
      return <PvPScreen />;

    case 'pvpTeamSelect':
      return <PvPTeamSelectScreen />;

    case 'pvpQueue':
      return <PvPQueueScreen />;

    case 'pvpReplay':
      return <PvPReplayScreen />;

    case 'pvpLeaderboard':
      return <PvPLeaderboardScreen />;

    case 'guide':
      return <GameGuideScreen />;

    case 'profile':
      return <ProfileScreen />;

    case 'debugSprites':
      return <DebugSpriteTuner onBack={() => setGameState('menu')} />;

    // Trials Screens
    case 'trials':
      return <TrialsScreen />;

    case 'trialDetail':
      return <TrialDetailScreen />;

    case 'trialBattle':
      return <TrialBattleScreen />;

    default:
      return <MenuScreen />;
  }
};

/**
 * GameDataLoadingGate
 *
 * Shows a loading screen until game data is loaded from the backend
 */
const GameDataLoadingGate = ({ children }) => {
  const { loading, error, isReady } = useGameData();

  if (loading && !isReady) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading game data...</p>
        </div>
      </div>
    );
  }

  if (error && !isReady) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center p-6 bg-red-900/50 rounded-lg max-w-md">
          <p className="text-red-400 text-lg mb-4">Failed to load game data</p>
          <p className="text-gray-400 text-sm mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return children;
};

/**
 * Main App Component (Authenticated)
 *
 * Only rendered after successful authentication
 */
const AuthenticatedApp = () => {
  return (
    <GameDataLoadingGate>
      <div className="w-full min-h-screen">
        <BattleController />
        <GameRouter />
      </div>
    </GameDataLoadingGate>
  );
};

/**
 * Root App Component
 *
 * Entry point - sets up providers and authentication gate
 */
export default function App() {
  return (
    <AppProviders>
      <AuthWrapper>
        <AuthenticatedApp />
      </AuthWrapper>
    </AppProviders>
  );
}

